import { useEffect, useState } from "react";
import useEvent from "react-use-event-hook";

import { IAddressSearchBounds } from "@ryde/shared";

import { BOUNDS_EMPTY_EDIT_BASE, TBoundsCorner } from "./bounds-block.const";
import {
  isSameBounds,
  isValidAddressSearchBounds,
  moveBoundsCorner,
} from "./bounds-block.utils";

interface IUseBoundsBlockEditorArgs {
  value: IAddressSearchBounds | undefined;
  defaultBounds: IAddressSearchBounds | undefined;
  /** `null` clears the parameter. `undefined` would be dropped from the request body. */
  onChange: (value: IAddressSearchBounds | null) => void;
}

export interface IBoundsBlockEditor {
  draft: IAddressSearchBounds | undefined;
  isEditing: boolean;
  isSaveable: boolean;
  /** False when there is no default, or when the draft already is it — nothing to reset to. */
  canResetToDefault: boolean;
  startEditing: () => void;
  cancelEditing: () => void;
  save: () => void;
  resetToDefault: () => void;
  clear: () => void;
  changeEdge: (edge: keyof IAddressSearchBounds, edgeValue: number) => void;
  moveCorner: (args: {
    corner: TBoundsCorner;
    latitude: number;
    longitude: number;
  }) => void;
}

/**
 * Edit state for one rectangle.
 *
 * The map and the numbers edit the same draft: a corner handle moves two of its edges, a
 * typed field moves one, and the rectangle on the map is drawn from the draft — so both
 * directions are live and neither has to be read back from the map. Nothing reaches the form
 * until Save, so an abandoned edit leaves the saved rectangle alone.
 */
export const useBoundsBlockEditor = ({
  value,
  defaultBounds,
  onChange,
}: IUseBoundsBlockEditorArgs): IBoundsBlockEditor => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<IAddressSearchBounds | undefined>(value);

  /*
   * The form fills in after the fields mount — the settings page renders the map before the
   * saved params arrive — so the initial `useState(value)` is usually `undefined`. Without
   * this the rectangle would never appear and Edit would open the default instead of what
   * the company actually saved. Skipped while editing, so it cannot overwrite a draft.
   */
  useEffect(() => {
    if (!isEditing) {
      setDraft(value);
    }
  }, [isEditing, value]);

  const startEditing = useEvent((): void => {
    setDraft(value ?? defaultBounds);
    setIsEditing(true);
  });

  const cancelEditing = useEvent((): void => {
    setDraft(value);
    setIsEditing(false);
  });

  const save = useEvent((): void => {
    if (!isValidAddressSearchBounds(draft)) {
      return;
    }

    onChange(draft);
    setIsEditing(false);
  });

  const resetToDefault = useEvent((): void => {
    setDraft(defaultBounds);
  });

  const clear = useEvent((): void => {
    setDraft(undefined);
    // Explicit null, not undefined: the parameter has to be sent to be unset.
    onChange(null);
    setIsEditing(false);
  });

  const changeEdge = useEvent(
    (edge: keyof IAddressSearchBounds, edgeValue: number): void => {
      // With nothing loaded there is still something to type into, so the edges start at a
      // zero box. It cannot be saved until it encloses an area, which the Save guard checks.
      const base = draft ?? defaultBounds ?? BOUNDS_EMPTY_EDIT_BASE;

      setDraft({ ...base, [edge]: edgeValue });
    },
  );

  const moveCorner = useEvent(
    ({
      corner,
      latitude,
      longitude,
    }: {
      corner: TBoundsCorner;
      latitude: number;
      longitude: number;
    }): void => {
      // Only a loaded rectangle has corners to drag, so there is nothing to fall back to.
      if (!draft) {
        return;
      }

      setDraft(
        moveBoundsCorner({ bounds: draft, corner, latitude, longitude }),
      );
    },
  );

  return {
    draft,
    isEditing,
    isSaveable: isValidAddressSearchBounds(draft),
    canResetToDefault:
      Boolean(defaultBounds) && !isSameBounds(draft, defaultBounds),
    startEditing,
    cancelEditing,
    save,
    resetToDefault,
    clear,
    changeEdge,
    moveCorner,
  };
};
